<template>
  <div
    data-test-id="showtimes-date-filter"
  >
    <slot name="left-column" />
    <template v-if="showDateFilter">
      <div
        v-if="showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.TOGGLE || desktopRegisterLayout"
        class="col-auto"
        :class="{ 'text-center': centerAlignDateFilter }"
        :style="{
          'padding-right': centerAlignDateFilter && hasFilter && !isMobileView ? '60px' : undefined,
          'padding-left': centerAlignDateFilter && !desktopRegisterLayout && showtimesDisplayTypeAllowToggle && !isMobileView ? '60px' : undefined
        }"
      >
        <date-toggle-selector
          :size="desktopRegisterLayout ? 'md' : 'md'"
          unelevated
          toggle-color="white"
          toggle-text-color="primary"
          :text-color="isShowtimesDarkMode ? 'responsive' : 'dark'"
          :q-date-options="allowPastShows ? undefined : formattedDates"
          class="q-pa-xs bg-responsive-grey-2"
          :class="{ 'q-mx-md': desktopRegisterLayout, 'q-my-md': !desktopRegisterLayout }"
          :all-movies="allMovies"
          :show-date-filter="showDateFilter"
          :showtimes-date-picker-type="showtimesDatePickerType"
          :initial-date="date"
          :dates-with-showing="dates"
          :custom-date="customDate"
          :date-toggle-allow-all="toggleAllowAll"
          :date="date"
          :date-selection="dateSelection"
          @date-selection="value => $emit('date-selection', value)"
          @date="value => $emit('date', value)"
          @show-calendar="value => $emit('show-calendar', value)"
        />
      </div>
      <date-selector
        v-else-if="showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.SELECTOR"
        v-model="showtimesDate"
        class="col"
        :compact="!!addFixedPosition"
        :border-radius="borderRadius"
        :is-dark-mode="configureDarkMode"
        :options="formattedDates"
        :align-right="!centerAlignDateFilter"
        :is-phone-preview="isPhonePreview"
        :style="{
          'padding-right': centerAlignDateFilter && hasFilter && !isMobileView ? '60px' : undefined,
          'padding-left': centerAlignDateFilter && !desktopRegisterLayout && showtimesDisplayTypeAllowToggle && !isMobileView ? '60px' : undefined
        }"
      />
      <date-selector-swiper
        v-else
        v-model="showtimesDate"
        :compact="!!addFixedPosition"
        :border-radius="borderRadius"
        :is-dark-mode="configureDarkMode"
        :options="formattedDates"
        :align-right="!centerAlignDateFilter"
        :is-phone-preview="isPhonePreview"
        class="col"
      />
    </template>
    <slot name="right-column" />
  </div>
</template>

<script>
import { date } from 'quasar'
import { SHOWTIMES_DATE_PICKER_TYPES } from 'src/models/page/Section'
import { defineAsyncComponent } from 'vue'
const { formatDate } = date

export default {
  name: 'ShowtimesDateFilter',
  components: {
    DateSelector: defineAsyncComponent(() => import('src/modules/consumer/components/date-filter/DateSelector')),
    DateSelectorSwiper: defineAsyncComponent(() => import('src/modules/consumer/components/date-filter/DateSelectorSwiper')),
    DateToggleSelector: defineAsyncComponent(() => import('src/modules/consumer/components/date-filter/DateToggleSelector'))
  },
  context: ['currentUser'],
  configuration: ['currentSite'],
  props: {
    initialDate: {
      type: String,
      required: false,
      default: null
    },
    showDateFilter: {
      type: Boolean,
      required: false,
      default: true
    },
    allMovies: {
      type: Boolean,
      required: true
    },
    allowPastShows: {
      type: Boolean,
      default: false
    },
    isMoviePage: {
      type: Boolean,
      required: false,
      default: false
    },
    showtimesDatePickerType: {
      type: String,
      required: false,
      default: SHOWTIMES_DATE_PICKER_TYPES.TOGGLE
    },
    showtimesDisplayTypeAllowToggle: {
      type: Boolean,
      required: false,
      default: false
    },
    isShowtimesDarkMode: {
      type: Boolean,
      required: false,
      default: false
    },
    showtimesAllowAll: {
      type: Boolean,
      required: false,
      default: false
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 0
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    useMobileRegisterLayout: {
      type: Boolean,
      required: false,
      default: false
    },
    addFixedPosition: {
      type: Boolean,
      required: false,
      default: false
    },
    hasFilter: {
      type: Boolean,
      required: false,
      default: false
    },
    centerAlignDateFilter: {
      type: Boolean,
      required: false,
      default: false
    },
    datesWithShowing: {
      type: Object,
      required: true,
      default: () => { return {} }
    },
    formattedDatesWithShowing: {
      type: Array,
      required: false,
      default: () => []
    },
    date: {
      type: String,
      required: false,
      default: null
    },
    dateSelection: {
      type: String,
      required: false,
      default: null
    },
    customDate: {
      type: Boolean,
      required: false,
      default: false
    },
    initialSiteIds: {
      type: Array,
      required: false,
      default: null
    },
    displayDatesWithoutShowings: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['date-selection', 'date', 'show-calendar'],
  data () {
    return {
      showtimesDate: (this.initialDate || this.currentSite.today)
    }
  },
  computed: {
    configureDarkMode () {
      if (this.addFixedPosition) return this.$isColorDark(this.currentSite.backgroundColor)
      return this.isShowtimesDarkMode
    },
    desktopRegisterLayout () {
      return this.$register && !this.useMobileRegisterLayout
    },
    mockDates () {
      const today = new Date()
      const nextTwoDays = Array.from({ length: 2 }, (_, i) => {
        const date = new Date(today)
        return date.setDate(today.getDate() + i)
      })

      const threeDaysAWeekFromNow = Array.from({ length: 3 }, (_, i) => {
        const date = new Date(today)
        return date.setDate(today.getDate() + 7 + i)
      })
      return [...nextTwoDays, ...threeDaysAWeekFromNow]
    },
    formattedDates () {
      if (this.displayDatesWithoutShowings && this.formattedDatesWithShowing.length === 0) {
        return this.mockDates.map(date => this.formatDate(date, 'YYYY/MM/DD'))
      }
      return this.formattedDatesWithShowing
    },
    dates () {
      if (this.displayDatesWithoutShowings && this.formattedDatesWithShowing.length === 0) {
        return { value: this.mockDates.map(date => this.formatDate(date, 'YYYY-MM-DD')) }
      }
      return this.datesWithShowing
    },
    toggleAllowAll () {
      if (this.$register) return this.isMoviePage // always allow selecting all in register movie pages
      return (this.isMoviePage && !this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites && !!this.initialSiteIds) || (this.showtimesAllowAll && this.showDateFilter && this.showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.TOGGLE)
    }
  },
  watch: {
    initialDate (val) {
      // this is to update the date selector to the next showing date
      this.showtimesDate = val
    },
    showtimesDate () {
      this.showCalendar = false
      this.$emit('date', this.showtimesDate)
    }
  },

  created () {
    _.extend(this, {
      SHOWTIMES_DATE_PICKER_TYPES,
      formatDate
    })
  }

}
</script>
